'use client';

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2, Hash, Mail, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';
import type { AuthRole } from './AuthPageClient';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface CodeLoginFormData {
  attendanceCode: string;
}

export default function LoginForm({ role, redirectTo }: { role: AuthRole; redirectTo?: string }) {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginMode, setLoginMode] = useState<'email' | 'code'>('email');
  const [codeLoginError, setCodeLoginError] = useState('');
  // Forgot password state
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({ defaultValues: { rememberMe: false } });

  const {
    register: registerCode,
    handleSubmit: handleSubmitCode,
    formState: { errors: codeErrors },
  } = useForm<CodeLoginFormData>();

  // Restore remembered session on mount (30-day remember me)
  useEffect(() => {
    const stored = localStorage.getItem('smartbls_remember_session');
    if (!stored) return;
    try {
      const { access_token, refresh_token, expires_at } = JSON.parse(stored);
      if (Date.now() > expires_at) {
        localStorage.removeItem('smartbls_remember_session');
        return;
      }
      const supabase = createClient();
      supabase.auth.setSession({ access_token, refresh_token }).then(({ data, error }) => {
        if (error || !data.session) {
          localStorage.removeItem('smartbls_remember_session');
          return;
        }
        const userRole = data.session.user?.user_metadata?.role || 'student';
        if (userRole === 'admin') {
          router.push('/admin-dashboard');
        } else {
          router.push('/student-learning-portal');
        }
      });
    } catch {
      localStorage.removeItem('smartbls_remember_session');
    }
  }, [router]);

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const supabase = createClient();

      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (error) {
        toast.error('Invalid email or password. Please check your credentials and try again.');
        setIsLoading(false);
        return;
      }

      // Remember Me: persist session tokens for 30 days
      if (data.rememberMe && authData.session) {
        const expiresAt = Date.now() + 30 * 24 * 60 * 60 * 1000; // 30 days
        localStorage.setItem('smartbls_remember_session', JSON.stringify({
          access_token: authData.session.access_token,
          refresh_token: authData.session.refresh_token,
          expires_at: expiresAt,
        }));
      } else {
        // Clear any previously stored remember-me session
        localStorage.removeItem('smartbls_remember_session');
      }

      const userRole = authData.user?.user_metadata?.role || 'student';

      if (userRole === 'student') {
        const meta = authData.user?.user_metadata || {};
        localStorage.setItem('smartbls_student_session', JSON.stringify({
          name: meta.full_name || '',
          className: meta.class_name || '',
          school: meta.school_name || '',
          email: authData.user?.email || '',
          attendanceCode: meta.attendance_code || '',
        }));
      }

      toast.success('Welcome back! Redirecting...');

      if (redirectTo) {
        router.push(redirectTo);
      } else if (userRole === 'admin') {
        router.push('/admin-dashboard');
      } else {
        router.push('/student-learning-portal');
      }
    } catch (err: unknown) {
      toast.error((err as Error)?.message || 'Something went wrong. Please try again.');
      setIsLoading(false);
    }
  };

  const onCodeSubmit = async (data: CodeLoginFormData) => {
    setIsLoading(true);
    setCodeLoginError('');
    try {
      // Call the server-side API route — works reliably on every device
      const res = await fetch('/api/code-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: data.attendanceCode.trim() }),
      });

      const result = await res.json();

      if (!res.ok || !result.success) {
        let errorMessage = result.error || 'Login failed. Please check your code and try again.';
        // Map status codes to specific user-friendly messages
        if (res.status === 400) {
          errorMessage = 'Invalid code. Please enter a valid 4-digit number.';
        } else if (res.status === 404) {
          errorMessage = 'No student found with that code. Please double-check your code or ask your teacher.';
        } else if (res.status === 500) {
          errorMessage = result.error || 'Unable to sign in right now. Please try again in a moment.';
        }
        setCodeLoginError(errorMessage);
        toast.error(errorMessage);
        setIsLoading(false);
        return;
      }

      // Set the session in the Supabase client using the tokens from the server
      const supabase = createClient();
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: result.session.access_token,
        refresh_token: result.session.refresh_token,
      });

      if (sessionError) {
        const msg = 'Session setup failed. Please try again.';
        setCodeLoginError(msg);
        toast.error(msg);
        setIsLoading(false);
        return;
      }

      // Save student info to localStorage for the portal
      localStorage.setItem('smartbls_student_session', JSON.stringify(result.student));

      toast.success('Welcome! Redirecting to your portal...');
      router.push('/student-learning-portal');
    } catch {
      const msg = 'Network error. Please check your connection and try again.';
      setCodeLoginError(msg);
      toast.error(msg);
      setIsLoading(false);
    }
  };

  const sendResetEmail = async () => {
    if (!forgotEmail || !/^\S+@\S+\.\S+$/.test(forgotEmail)) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setForgotLoading(true);
    try {
      const supabase = createClient();
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) {
        toast.error(error.message || 'Failed to send reset email. Please try again.');
      } else {
        setForgotSent(true);
      }
    } catch (err: unknown) {
      toast.error((err as Error)?.message || 'Something went wrong. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgotModal = () => {
    setShowForgotModal(false);
    setForgotEmail('');
    setForgotSent(false);
    setForgotLoading(false);
  };

  const isStudentRole = role === 'student' && !redirectTo;

  return (
    <div className="space-y-5 animate-slide-up">
      <div>
        <p className="text-xl font-bold text-foreground mb-1">
          {role === 'admin' ? 'Admin Sign In' : 'Welcome back'}
        </p>
        <p className="text-sm text-muted-foreground">
          {role === 'admin' ? 'Sign in to manage your BLS training event' : 'Sign in to continue your BLS training journey'}
        </p>
      </div>

      {/* Login mode toggle — only for students */}
      {isStudentRole && (
        <div className="flex bg-muted rounded-xl p-1">
          <button
            type="button"
            onClick={() => setLoginMode('email')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-semibold transition-all duration-200 ${
              loginMode === 'email' ? 'bg-card text-primary shadow-card' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Mail size={14} />
            Email Login
          </button>
          <button
            type="button"
            onClick={() => setLoginMode('code')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-semibold transition-all duration-200 ${
              loginMode === 'code' ? 'bg-card text-primary shadow-card' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Hash size={14} />
            Use Code
          </button>
        </div>
      )}

      {/* Code-only login form */}
      {isStudentRole && loginMode === 'code' ? (
        <form onSubmit={handleSubmitCode(onCodeSubmit)} className="space-y-5">
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
            <p className="text-xs text-primary font-semibold mb-1">Enter your attendance code</p>
            <p className="text-xs text-muted-foreground">
              Enter the 4-digit attendance code given by your teacher or from your registration confirmation.
            </p>
          </div>

          {/* Inline error banner for code login */}
          {codeLoginError && (
            <div className="flex items-start gap-3 p-3 rounded-xl bg-danger/10 border border-danger/30 animate-slide-up">
              <AlertCircle size={16} className="text-danger flex-shrink-0 mt-0.5" />
              <p className="text-sm text-danger font-medium">{codeLoginError}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">
              Your 4-Digit Attendance Code
            </label>
            <div className="relative">
              <Hash size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                inputMode="numeric"
                maxLength={4}
                placeholder="e.g. 7342"
                className={`w-full pl-9 pr-4 py-3 rounded-lg border text-sm bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all text-center text-xl font-bold tracking-widest ${
                  codeErrors.attendanceCode ? 'border-danger' : 'border-border'
                }`}
                {...registerCode('attendanceCode', {
                  required: 'Attendance code is required',
                  pattern: { value: /^\d{4}$/, message: 'Enter a valid 4-digit code' },
                  onChange: () => setCodeLoginError(''),
                })}
              />
            </div>
            {codeErrors.attendanceCode && (
              <p className="text-danger text-xs mt-1">{codeErrors.attendanceCode.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 rounded-lg gradient-primary text-white font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-150 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed hover:shadow-card-md"
          >
            {isLoading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Signing in...
              </>
            ) : (
              'Enter Portal with Code'
            )}
          </button>
        </form>
      ) : (
        /* Standard email + password form */
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Email */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">
              Email Address
            </label>
            <input
              type="email"
              placeholder="you@example.com"
              className={`w-full px-4 py-2.5 rounded-lg border text-sm bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all ${
                errors.email ? 'border-danger' : 'border-border'
              }`}
              {...register('email', {
                required: 'Email is required',
                pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
              })}
            />
            {errors.email && (
              <p className="text-danger text-xs mt-1">{errors.email.message}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-semibold text-foreground">
                Password
              </label>
              <button
                type="button"
                onClick={() => { setShowForgotModal(true); setForgotSent(false); }}
                className="text-xs text-primary hover:underline font-semibold"
              >
                Forgot password?
              </button>
            </div>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                className={`w-full px-4 py-2.5 pr-10 rounded-lg border text-sm bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all ${
                  errors.password ? 'border-danger' : 'border-border'
                }`}
                {...register('password', {
                  required: 'Password is required',
                  minLength: { value: 6, message: 'Password must be at least 6 characters' },
                })}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.password && (
              <p className="text-danger text-xs mt-1">{errors.password.message}</p>
            )}
          </div>

          {/* Remember Me */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="rememberMe"
              className="w-4 h-4 rounded border-border accent-primary"
              {...register('rememberMe')}
            />
            <label htmlFor="rememberMe" className="text-sm text-muted-foreground cursor-pointer">
              Remember me for 30 days
            </label>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 rounded-lg gradient-primary text-white font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-150 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed hover:shadow-card-md"
          >
            {isLoading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </button>

          {/* Contact info for access */}
          <p className="text-center text-xs text-muted-foreground">
            Don&apos;t have an account?{' '}
            <span className="font-semibold text-foreground">Register using the Register tab above</span>
            {' '}or contact{' '}
            <a href="mailto:iapcoimbatore@gmail.com" className="text-primary hover:underline font-semibold">
              iapcoimbatore@gmail.com
            </a>
          </p>
        </form>
      )}

      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card rounded-2xl shadow-xl w-full max-w-sm p-6 relative animate-slide-up">
            <button
              type="button"
              onClick={closeForgotModal}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>

            {forgotSent ? (
              <div className="text-center space-y-4">
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                  <Mail size={28} className="text-green-600" />
                </div>
                <div>
                  <p className="text-base font-bold text-foreground mb-1">Check your inbox</p>
                  <p className="text-sm text-muted-foreground">
                    A password reset link has been sent to{' '}
                    <span className="font-semibold text-foreground">{forgotEmail}</span>.
                    Check your email and follow the link to reset your password.
                  </p>
                </div>
                <p className="text-xs text-muted-foreground">
                  Didn&apos;t receive it? Check your spam folder or{' '}
                  <button
                    type="button"
                    onClick={() => setForgotSent(false)}
                    className="text-primary hover:underline font-semibold"
                  >
                    try again
                  </button>.
                </p>
                <button
                  type="button"
                  onClick={closeForgotModal}
                  className="w-full py-2.5 rounded-lg gradient-primary text-white font-semibold text-sm transition-all duration-150 active:scale-95"
                >
                  Done
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <p className="text-base font-bold text-foreground mb-1">Reset your password</p>
                  <p className="text-sm text-muted-foreground">
                    Enter the email address you used to register. We&apos;ll send you a link to reset your password.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full px-4 py-2.5 rounded-lg border border-border text-sm bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                    onKeyDown={(e) => { if (e.key === 'Enter') sendResetEmail(); }}
                  />
                </div>

                <button
                  type="button"
                  onClick={sendResetEmail}
                  disabled={forgotLoading}
                  className="w-full py-2.5 rounded-lg gradient-primary text-white font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-150 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {forgotLoading ? (
                    <>
                      <Loader2 size={15} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </button>

                <p className="text-center text-xs text-muted-foreground">
                  Remembered your password?{' '}
                  <button
                    type="button"
                    onClick={closeForgotModal}
                    className="text-primary hover:underline font-semibold"
                  >
                    Back to sign in
                  </button>
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}